// LA COMPROVACIÓ D'UNITATS.
//
// Fora de React i sense cap import tret d'`expressions.js`. Es prova sola: `npm run prova:unitats`.
//
// ── PER QUÈ VAL LA PENA ──────────────────────────────────────────────────────────────────────
//
// **La meitat dels errors d'un model novell són d'unitats**, i cap no es veu mirant la pantalla:
// el model corre igual de bé i dona números que semblen bons. Multiplicar litres per litres i
// abocar-ho a un dipòsit de litres no peta enlloc — simplement el model deixa de voler dir res.
//
// I hi ha una regla que aquesta comprovació fa complir i que és **el mateix contingut de la
// notació escrit en unitats**:
//
// > **Un estoc guarda una quantitat. Un flux és una TAXA.** Si l'estoc és en litres, tot el que
// > hi entri o en surti ha de ser en litres per unitat de temps. No hi ha excepcions, i quan
// > algú n'escriu una és que ha confós les dues coses.
//
// Per això aquí no es comprova només que les unitats quadrin dins d'una expressió: es comprova
// **la relació entre el flux i l'estoc que toca**, que és on hi ha la lliçó.
//
// ── COM ES REPRESENTA UNA UNITAT ─────────────────────────────────────────────────────────────
//
// Un mapa de dimensió a exponent. `l/min` és `{l: 1, min: -1}`; `m^2` és `{m: 2}`; un número sense
// unitat és `{}`. Multiplicar suma exponents, dividir els resta, i comparar és mirar si els dos
// mapes són iguals. No hi ha taula de conversions ni sistema d'unitats: **no es tradueixen
// quilòmetres a metres**, es comprova que la gramàtica quadri. Un model d'aula parla de gent, de
// diners i de confiança tan sovint com de metres.
//
// ── EL QUE NO ES FA, I ÉS UNA DECISIÓ ────────────────────────────────────────────────────────
//
// **Una unitat en blanc no és cap error.** Un model a mig fer en té la meitat sense posar, i un
// avís per cada una seria soroll que s'aprèn a ignorar de seguida. El desconegut es propaga com a
// desconegut i calla. Qui vulgui la comprovació sencera, que ompli les unitats.

import { analitza, referencies, REF_TEMPS } from './expressions';

const buida = () => ({});
const esBuida = (u) => Object.keys(u).length === 0;

export function multiplica(a, b) {
  const r = { ...a };
  for (const [d, e] of Object.entries(b)) { r[d] = (r[d] || 0) + e; if (!r[d]) delete r[d]; }
  return r;
}
export function inverteix(u) {
  const r = {};
  for (const [d, e] of Object.entries(u)) r[d] = -e;
  return r;
}
export const divideix = (a, b) => multiplica(a, inverteix(b));
export const igual = (a, b) => escriu(a) === escriu(b);

// Com s'ensenya una unitat. Ordre estable perquè el mateix conjunt doni sempre el mateix text:
// si no, dos avisos idèntics es llegirien com a diferents.
export function escriu(u) {
  const dalt = Object.entries(u).filter(([, e]) => e > 0).sort();
  const baix = Object.entries(u).filter(([, e]) => e < 0).sort();
  const tros = ([d, e]) => (Math.abs(e) === 1 ? d : `${d}^${Math.abs(e)}`);
  const n = dalt.length ? dalt.map(tros).join('·') : '1';
  return baix.length ? `${n}/${baix.map(tros).join('·')}` : n;
}

// ── LLEGIR UNA UNITAT ESCRITA ────────────────────────────────────────────────────────────────
//
// `l/min`, `1/min`, `m^2`, `€/(persona·mes)`. El punt volat i l'asterisc valen igual, que segons
// el teclat surt l'un o l'altre.
export function llegeix(text) {
  const t = String(text || '').trim();
  if (!t) return { desconeguda: true };
  const tk = t.match(/[A-Za-zÀ-ÿ€$£%_][A-Za-zÀ-ÿ0-9€$£%_]*|\d+|[*·/^()]/g);
  if (!tk || tk.join('').length !== t.replace(/\s/g, '').length) return { error: t };
  let p = 0;

  const factor = () => {
    if (tk[p] === '(') {
      p += 1;
      const dins = producte();
      if (dins.error) return dins;
      if (tk[p] !== ')') return { error: t };
      p += 1;
      return dins;
    }
    const nom = tk[p];
    if (nom === undefined || /[*·/^()]/.test(nom)) return { error: t };
    p += 1;
    // `1` vol dir «sense unitat», que és com s'escriu una freqüència: `1/min`.
    let u = /^\d+$/.test(nom) ? buida() : { [nom.toLowerCase()]: 1 };
    if (tk[p] === '^') {
      p += 1;
      const e = Number(tk[p]);
      if (!Number.isInteger(e)) return { error: t };
      p += 1;
      const r = {};
      for (const [d, x] of Object.entries(u)) r[d] = x * e;
      u = r;
    }
    return { u };
  };

  const producte = () => {
    let acum = factor();
    if (acum.error) return acum;
    while (tk[p] === '*' || tk[p] === '·' || tk[p] === '/') {
      const op = tk[p]; p += 1;
      const seg = factor();
      if (seg.error) return seg;
      acum = { u: op === '/' ? divideix(acum.u, seg.u) : multiplica(acum.u, seg.u) };
    }
    return acum;
  };

  const r = producte();
  if (r.error || p < tk.length) return { error: t };
  return { u: r.u };
}

// ── LES UNITATS D'UNA EXPRESSIÓ ──────────────────────────────────────────────────────────────
//
// De baix cap amunt. Multiplicar i dividir sempre es poden; **sumar i restar, només si les dues
// bandes són el mateix**, i és aquí on surten la meitat de les troballes: `Dipòsit + Cabal` és
// litres més litres per minut, i no vol dir res encara que el número surti.
export function unitatsDe(arbre, perId) {
  if (!arbre) return { desconeguda: true };
  switch (arbre.mena) {
    case 'num': return { u: buida() };
    case 'ref': {
      const u = perId[arbre.valor];
      return u ? { u } : { desconeguda: true };
    }
    case 'neg': return unitatsDe(arbre.d, perId);
    case 'op': {
      const a = unitatsDe(arbre.e, perId), b = unitatsDe(arbre.d, perId);
      if (a.conflicte) return a;
      if (b.conflicte) return b;
      if (arbre.op === '*' || arbre.op === '/') {
        if (a.desconeguda || b.desconeguda) return { desconeguda: true };
        return { u: arbre.op === '*' ? multiplica(a.u, b.u) : divideix(a.u, b.u) };
      }
      // Suma i resta.
      if (a.desconeguda) return b.desconeguda ? { desconeguda: true } : b;
      if (b.desconeguda) return a;
      if (!igual(a.u, b.u)) return { conflicte: { esquerra: escriu(a.u), dreta: escriu(b.u) } };
      return { u: a.u };
    }
    default: return { desconeguda: true };
  }
}

// ── LA COMPROVACIÓ SENCERA ───────────────────────────────────────────────────────────────────
//
// Retorna una llista d'avisos amb clau de traducció. **Són avisos i no errors**: un model amb
// unitats regirades corre igual, i impedir-ho seria impedir treballar a mig fer. Qui decideix si
// val la pena arreglar-ho és qui el dibuixa.
// LA UNITAT DE TEMPS DEL MODEL, deduïda dels seus fluxos. Si un dipòsit és en litres i el que
// hi entra és en litres per minut, el temps d'aquest model es compta en minuts, i no cal que
// ningú ho declari enlloc: ja ho diu el dibuix.
//
// Viu a part perquè la fan servir dues coses —la comprovació i l'eix del gràfic— i abans l'una
// cridava l'altra, cosa que amb el temps com a variable hauria estat una volta rodona.
export function dedueixTemps(components) {
  const trobades = new Set();
  for (const f of components.filter((x) => x.type === 'flow')) {
    const uf = llegeix(f.properties?.units);
    if (uf.error || uf.desconeguda) continue;
    for (const extrem of [f.properties?.from, f.properties?.to]) {
      const s = components.find((x) => String(x.id) === String(extrem) && x.type === 'stock');
      if (!s) continue;
      const us = llegeix(s.properties?.units);
      if (us.error || us.desconeguda) continue;
      const dims = Object.entries(divideix(uf.u, us.u));
      if (dims.length === 1 && dims[0][1] === -1) trobades.add(dims[0][0]);
    }
  }
  return trobades.size === 1 ? [...trobades][0] : null;
}

export function comprovaUnitats(components) {
  const avisos = [];
  const perId = {};
  const nom = (id) => components.find((c) => String(c.id) === String(id))?.name || '?';

  // LES UNITATS DEL TEMPS. Són les que el model deixa deduir; si no se'n pot deduir cap —perquè
  // no n'hi ha prou de posades, o perquè n'hi ha de barrejades— el temps queda DESCONEGUT i
  // qualsevol expressió que l'anomeni calla, en comptes de treure un avís inventat.
  const uTemps = dedueixTemps(components);
  if (uTemps) perId[REF_TEMPS] = { [uTemps]: 1 };

  for (const c of components) {
    if (c.type === 'cloud' || c.type === 'connector') continue;
    const r = llegeix(c.properties?.units);
    if (r.error) { avisos.push({ id: c.id, clau: 'sf.uIl·legible', detall: r.error }); continue; }
    if (!r.desconeguda) perId[String(c.id)] = r.u;
  }

  // 1. Que l'equació de cada flux i cada convertidor doni el que diu que dona.
  for (const c of components.filter((x) => x.type === 'flow' || x.type === 'converter')) {
    const eq = c.properties?.equation;
    if (!eq || !String(eq).trim()) continue;
    const a = analitza(eq);
    if (a.error) continue;                        // ja ho diu el motor, no cal dir-ho dos cops
    const u = unitatsDe(a.arbre, perId);
    if (u.conflicte) {
      avisos.push({ id: c.id, clau: 'sf.uSuma', ...u.conflicte });
      continue;
    }
    const declarada = perId[String(c.id)];
    if (!declarada || u.desconeguda) continue;
    // Una constant sense unitats («0.1») no contradiu res: és el cas normal d'una taxa que es
    // declara `1/min` i s'escriu com un número.
    if (esBuida(u.u) && !referencies(a.arbre).size) continue;
    if (!igual(u.u, declarada)) {
      avisos.push({ id: c.id, clau: 'sf.uEquacio', declarada: escriu(declarada), calculada: escriu(u.u) });
    }
  }

  // 2. LA REGLA DE FONS: un flux és una taxa de l'estoc que toca.
  const temps = new Map();      // unitat de temps → qui la fa servir
  for (const f of components.filter((x) => x.type === 'flow')) {
    const uf = perId[String(f.id)];
    if (!uf) continue;
    for (const extrem of [f.properties?.from, f.properties?.to]) {
      const s = components.find((x) => String(x.id) === String(extrem) && x.type === 'stock');
      if (!s) continue;                       // un núvol no té unitats i no diu res
      const us = perId[String(s.id)];
      if (!us) continue;
      const rao = divideix(uf, us);           // ha de ser 1/temps i res més
      const dims = Object.entries(rao);
      if (dims.length !== 1 || dims[0][1] !== -1) {
        avisos.push({ id: f.id, clau: 'sf.uNoEsTaxa', flux: escriu(uf),
                      estoc: escriu(us), estocNom: nom(s.id), cal: `${escriu(us)}/temps` });
      } else {
        const [unitatTemps] = dims[0];
        if (!temps.has(unitatTemps)) temps.set(unitatTemps, []);
        temps.get(unitatTemps).push(nom(f.id));
      }
    }
  }

  // 3. Que tot el model parli de la mateixa unitat de temps. Barrejar `l/min` i `l/h` al mateix
  // dibuix és l'errada clàssica, i el model corre igual de bé donant números que no volen dir res.
  if (temps.size > 1) {
    avisos.push({ clau: 'sf.uTempsBarrejat',
      detall: [...temps.entries()].map(([u, qui]) => `${u} (${qui.join(', ')})`).join(' · ') });
  }

  return avisos;
}

// Serveix per etiquetar l'eix del gràfic. És `dedueixTemps` amb un altre nom, i es manté perquè
// és el que criden les pantalles.
export const unitatDeTemps = dedueixTemps;

const UNITATS = { llegeix, escriu, multiplica, divideix, igual, unitatsDe,
                  comprovaUnitats, unitatDeTemps, dedueixTemps };
export default UNITATS;
